//
// Copyright 2021 DXOS.org
//

import * as Schema from 'effect/Schema';

import { type MulticastObservable } from '@dxos/async';
import { type SpecificCredential } from '@dxos/credentials';
import { type Obj } from '@dxos/echo';
import { type EchoDatabase, type SpaceSyncState } from '@dxos/echo-client';
import { type PublicKey, type SpaceId } from '@dxos/keys';
import { type Messenger } from '@dxos/protocols';
import { type Invitation } from '@dxos/protocols/buf/dxos/client/invitation_pb';
import { type SpaceState } from '@dxos/protocols/buf/dxos/client/invitation_pb';
import {
  type Contact,
  type Space_PipelineState,
  type Space as SpaceData,
  type SpaceMember,
} from '@dxos/protocols/buf/dxos/client/services_pb';
import { type EdgeReplicationSetting } from '@dxos/protocols/buf/dxos/echo/metadata_pb';
import { type SpaceSnapshot } from '@dxos/protocols/buf/dxos/echo/snapshot_pb';
import { type MembershipPolicy } from '@dxos/protocols/buf/dxos/halo/credentials_pb';
import { type Credential, type Epoch, type SpaceMember_Role } from '@dxos/protocols/buf/dxos/halo/credentials_pb';
import { type SpacesService } from '@dxos/protocols/rpc';

import { type CancellableInvitation } from './invitations/index.ts';
import { type SpaceProperties } from './types/index.ts';

export type CreateEpochOptions = {
  migration?: SpacesService.Migration;
  automergeRootUrl?: string;
};

export type ExportSpaceOptions = {
  /**
   * Archive format.
   * @default SpacesService.SpaceArchiveFormat.enums.BINARY
   */
  format?: SpacesService.SpaceArchiveFormat;
};

export interface SpaceInternal {
  get db(): EchoDatabase;
  get data(): SpaceData;

  getCredentials(): Promise<Credential[]>;

  getEpochs(): Promise<SpecificCredential<Epoch>[]>;

  // TODO(dmaretskyi): Return epoch info.
  createEpoch(options?: CreateEpochOptions): Promise<void>;

  // TOOD(burdon): Start to factor out credentials.
  removeMember(memberKey: PublicKey): Promise<void>;

  export(options?: ExportSpaceOptions): Promise<SpacesService.SpaceArchive>;

  /**
   * Migrate space data to the latest version.
   */
  migrate(): Promise<void>;

  setEdgeReplicationPreference(setting: EdgeReplicationSetting): Promise<void>;

  /**
   * Waits until the space is fully synced with EDGE.
   * @throws If the EDGE sync is disabled.
   */
  syncToEdge(opts?: {
    onProgress: (state: SpaceSyncState.PeerState | undefined) => void;
    timeout?: number;
  }): Promise<void>;
}

export const SPACE_TAG = Symbol('dxos.client.protocol.Space');

export interface Space extends Messenger {
  readonly [SPACE_TAG]: true;

  /**
   * @deprecated Use `id`.
   */
  get key(): PublicKey;

  /**
   * Unique space identifier.
   */
  get id(): SpaceId;

  /**
   * Echo database.
   */
  get db(): EchoDatabase;

  // TODO(burdon): Replace with state?
  get isOpen(): boolean;

  /**
   * Immutable tags assigned at space creation time.
   * Available on closed spaces.
   */
  get tags(): string[];

  /**
   * Immutable membership policy assigned at space creation time.
   * Available on closed spaces.
   */
  get membershipPolicy(): MembershipPolicy;

  /**
   * Current state of the space.
   * The database is ready to be used in `SpaceState.SPACE_READY` state.
   * Presence is available in `SpaceState.SPACE_CONTROL_ONLY` state.
   */
  get state(): MulticastObservable<SpaceState>;

  /**
   * Properties object.
   */
  get properties(): Obj.OfShape<SpaceProperties>;

  /**
   * Current state of space pipeline.
   */
  get pipeline(): MulticastObservable<Space_PipelineState>;

  get invitations(): MulticastObservable<CancellableInvitation[]>;

  get members(): MulticastObservable<SpaceMember[]>;

  /**
   * @deprecated
   */
  // TODO(wittjosiah): Audit and remove. This should not be exposed (or marked as internal).
  get internal(): SpaceInternal;

  /**
   * Activates the space enabling the use of the database and starts replication with peers.
   * The setting is persisted on the local device.
   */
  // TODO(wittjosiah): Rename activate/deactivate?
  open(): Promise<void>;

  /**
   * Deactivates the space stopping replication with other peers.
   * The space will not auto-open on the next app launch.
   * The setting is persisted on the local device.
   */
  close(): Promise<void>;

  /**
   * Tombstones (soft-deletes) the space and replicates the deletion to the user's other devices.
   * Every device stops replicating and unloads the space. Underlying data is not removed until
   * garbage collection (future work). This is terminal: the space cannot be re-opened via {@link open}.
   */
  delete(): Promise<void>;

  /**
   * Waits until the space is in the ready state, with database initialized.
   */
  waitUntilReady(): Promise<this>;

  createSnapshot(): Promise<SpaceSnapshot>;

  // TODO(burdon): Create invitation?
  // TODO(burdon): Factor out membership, etc.
  share(options?: Partial<Invitation>): CancellableInvitation;
  /** Admits a known identity directly, without an invitation; the guest completes with `joinBySpaceKey`. */
  admitContact(contact: Contact, role?: SpaceMember_Role): Promise<void>;
  updateMemberRole(request: Omit<SpacesService.UpdateMemberRoleRequest, 'spaceKey'>): Promise<void>;
}

export const isSpace = (object: unknown): object is Space =>
  typeof object === 'object' && object != null && (object as Space)[SPACE_TAG] === true;

// TODO(burdon): Create lower-level definition (HasId, db, etc.) and move to @dxos/echo.
export const SpaceSchema: Schema.Codec<Space> = Schema.Any.pipe(
  Schema.refine(isSpace),
  Schema.annotate({ title: 'Space' }),
);
